import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DataService } from '../../shared/data.service';
import { Lease } from './lease.model';
import { Observable, Subject, from } from 'rxjs';
import { SearchRequest } from '../program-work/search-request.model';
import { SearchResult } from '../program-work/search-result.model';
import { LeaseType } from './lease-type.model';
import { LeaseTemplate } from './lease-template.model';
import { Commodity } from '../commodity/commodity.model';
import { RailcarLease } from '../railcar/railcar-lease.model';
import { Railcar } from '../railcar/railcar.model';
import { Notification } from '../../components/common/notification/notification.model';
import { LeaseDisposition } from './lease-disposition.model';
import { switchMap, concatMap } from '../../../../node_modules/rxjs/operators';
import { LeaseContactGroupedByType } from './lease-contact-override.model';
import { Contact } from '../facility/contact.model';
import { toUnspecifiedCentralStartOfDay } from '../../shared/date-utility';

@Injectable({providedIn: 'root'})
export class LeaseService extends DataService<Lease> {
	dispoRailcar: LeaseDisposition[] = [];
	constructor(protected httpClient: HttpClient) {
		super(httpClient, 'lease')
	}

	public getByCustomer(customerNumber: number): Observable<Lease[]> {
		return this.httpClient.get<Lease[]>(this.url + '/getByCustomer?customerNumber=' + customerNumber);
	}

	public getHistoricalLeasesByCustomer(customerId: number): Observable<Lease[]> {
		return this.httpClient.get<Lease[]>(this.url + '/getHistoricalLeasesByCustomer?customerId=' + customerId	);
	}

	public getRailcarHistoryByRailcarId(railcarId: number): Observable<RailcarLease[]> {
		return this.httpClient.get<RailcarLease[]>(this.url + '/getRailcarHistoryByRailcarId?railcarId=' + railcarId);
	}

	public getRailcarsByLeaseId(leaseId: number): Observable<any[]> {
		return this.httpClient.get<any[]>(this.url + '/getRailcarsByLeaseId?leaseId=' + leaseId);
	}

	public GetRailcarsToBeRemovedByLeaseID(leaseId: number, isFutureLease: boolean): Observable<any[]> {
		return this.httpClient.get<any[]>(this.url + '/getRailcarsToBeRemovedByLeaseID?leaseId=' + leaseId + '&isFutureLease='+ isFutureLease);
	}

	public searchLeases(searchText: SearchRequest): Observable<SearchResult> {
		return this.httpClient.post<SearchResult>(this.url + '/search' , searchText);
	}

	public suggestSearchText(searchReq: SearchRequest): Observable<string[]> {
		return this.httpClient.get<string[]>(this.url + '?term=' + searchReq.searchText +
		this.getFacets(searchReq) + (searchReq.sort ? '&sort=' + searchReq.sort : ''))
	}

	public getAllLeaseType(): Observable<LeaseType[]> {
		return this.httpClient.get<LeaseType[]>(this.url + '/getAllLeaseType');
	}

	public getAllLeaseTemplate(): Observable<LeaseTemplate[]> {
		return this.httpClient.get<LeaseTemplate[]>(this.url + '/getAllLeaseTemplate');
	}
	public getLeasesAndSplcsByRailcarId(railcarId: number): Observable<RailcarLease[]> {
		return this.httpClient.get<RailcarLease[]>(this.url + '/getLeasesAndSplcsByRailcarId?railcarId=' + railcarId)
	}

	public getAllCommodity(): Observable<Commodity[]> {
		return this.httpClient.get<Commodity[]>(this.url + '/getAllCommodity');
	}

	public save(lease: Lease, isExpirationDateChanged: boolean): Observable<Lease> {
		return this.httpClient.post<Lease>(this.url + '?isExpirationDateChanged=' + isExpirationDateChanged, lease);
	}

	public addRailcarsToLease(leaseId: number, railcarLeases: RailcarLease[], adviseCustomer: boolean): Observable<RailcarLease[]> {
		return this.httpClient.post<RailcarLease[]>(this.url + `/addRailcarsToLease?leaseId=${leaseId}` + '&adviseCustomer='+ adviseCustomer, railcarLeases);
	}

	public removeRailcarsFromLease(leaseId: number, railcars: Railcar[]): Observable<Lease> {
		return this.httpClient.post<Lease>(this.url + `/removeRailcarsFromLease?leaseId=${leaseId}`, railcars);
	}

	public findUnavailableRailcars(railcarIds: Array<number>): Observable<number[]> {
		return this.httpClient.post<Array<number>>(this.url + `/findUnavailableRailcars`, railcarIds);
	}

	public approveCpDate(sightingDate: Date, data: Notification): Observable<any> {
		return this.httpClient.post<any>(this.url + `/approveCpDate?sightingDate=` + sightingDate.toLocaleString(), data);
	}

	public getIsReturningRailcarsWithoutFutureLease(leaseId: number): Observable<RailcarLease[]> {
		return this.httpClient.get<RailcarLease[]>(this.url + '/getIsReturningRailcarsWithoutFutureLease?leaseId=' + leaseId);
	}

	public getRailcarLeaseByLeaseId(leaseId: number): Observable<RailcarLease[]> {
		return this.httpClient.get<RailcarLease[]>(this.url + '/getRailcarLeaseByLeaseId?leaseId=' + leaseId);
	}

	public saveDispositionChanges(dispoRailcar: LeaseDisposition): Observable<boolean> {
		if (!this.dispoRailcar.includes(dispoRailcar)) {
			this.dispoRailcar.push(dispoRailcar);
			return this.httpClient.post<boolean>(this.url + `/saveDispositionChanges`, dispoRailcar);
		}
	}

	public saveDispositionChangesSequentially(subject: Subject<LeaseDisposition[]>) {
		this.dispoRailcar = [];
		return subject.pipe(
			switchMap(val => from(val).pipe(
			concatMap(x => this.saveDispositionChanges(x))
			))
		);
	}

	public previewLeaseBillingChanges(leaseBillings: RailcarLease[]): Observable<any[]> {
		return this.httpClient.post<any[]>(this.url + '/previewLeaseBillingChanges', leaseBillings);
	}

	public saveLeaseBillingChanges(leaseBillings: RailcarLease[]): Observable<any> {
		return this.httpClient.post<any>(this.url + `/saveLeaseBillingChanges`, leaseBillings);
	}

	public getBySearch(searchTerm: string): Observable<Lease[]> {
		return this.httpClient.get<Lease[]>(this.url + '/getByFuzzySearch?searchTerm=' + searchTerm);
	}

	public getAllLeaseWithCustomer(): Observable<Lease[]> {
		return this.httpClient.get<Lease[]>(this.url + '/getAllLeaseWithCustomer');
	}

	public getLeaseCustomerContactByLeaseIds(leaseIds: number[]): Observable<LeaseContactGroupedByType[]> {
		return this.httpClient.post<LeaseContactGroupedByType[]>(this.url + '/getLeaseCustomerContactByLeaseIds', leaseIds);
	}

	public getLeaseCustomerContactByLeaseId(leaseId: number): Observable<LeaseContactGroupedByType[]> {
		return this.httpClient.get<LeaseContactGroupedByType[]>(this.url + '/getLeaseCustomerContactByLeaseId?leaseId='+leaseId);
	}

	public saveLeaseContactOverride(leaseContactGroupedByType: LeaseContactGroupedByType): Observable<any> {
		return this.httpClient.post<any>(this.url + '/saveLeaseContactOverride', leaseContactGroupedByType);
	}

	public getLeaseCustomerOverridenContactsByLeaseID( leaseId : number,  contactType:string):Observable<Contact[]> {
		return this.httpClient.get<Contact[]>(this.url+ '/getLeaseCustomerOverridenContactsByLeaseID?leaseId='+leaseId+'&contactType='+contactType);
	}

	public saveRenewal(newlease: Lease, oldLeaseId: number): Observable<any> {
		const payload: any = { ...newlease };
		if (newlease.inServiceDate) {
			payload.inServiceDate = toUnspecifiedCentralStartOfDay(newlease.inServiceDate);
		}
		return this.httpClient.post<Lease>(this.url + `/renewLease?oldLeaseId=${oldLeaseId}`, payload);
	}

	public extendLease(leaseId: number, monthExtended: number, sendAmendment: boolean)
	{
		return this.httpClient.post<boolean>(this.url + '/extendLease', leaseId,
			{
				params: {
					'leaseId': leaseId.toString(),
					'monthExtended': monthExtended.toString(),
					'sendAmendment': sendAmendment ? 'true':'false'
				},
			});
	}

	public GetExtensionAmendmentPdf(leaseId: number, monthExtended: number): Observable<string> {
		return this.httpClient.get(this.url + '/GetExtensionAmendmentPdf', 
			{ 
				params: {
					'leaseId': leaseId.toString(),
					'monthExtended': monthExtended.toString(),
				},
				responseType: 'text' 
			});
	}
	public GetRailcarEligibleForReplacementByRider(leaseId: number): Observable<Railcar[]> {
		return this.httpClient.get<Railcar[]>(this.url + '/getRailcarEligibleForReplacementByRider?leaseId=' + leaseId);
	}

	public getLeaseSearchDefaultColumns() {
		return [
			{ key: 'customerNumber', value: 'Customer#' },
			{ key: 'customerName', value: 'Customer Name' },
			{ key: 'commodity', value: 'Commodity' },
			{ key: 'expirationDate', value: 'Expiration Date' },
			{ key: 'term', value: 'Lease Term (Months)' },
			{ key: 'rentalRate', value: 'Rental Rate' },
			{ key: 'carsLeased', value: 'Cars Leased' },
			{ key: 'carsBilled', value: 'Cars Billed' },
			{ key: 'carsReturning', value: 'Cars Returning' },
			{ key: 'type', value: 'Lease Type' },
			{ key: 'salesDirector', value: 'Sales Director' },
			{ key: 'penaltyRate', value: 'Mileage Penalty Rate ' },
			{ key: 'maxMileage', value: 'Max Mileage/Car/Year' },
			{ key: 'leaseTemplate', value: 'Template' },
			{ key: 'notificationDays', value: 'Notification Days ' },
			{ key: 'rentalWaiver', value: 'Rental Waiver' },
			{ key: 'carsContracted', value: 'Cars Contracted' },
		]
	}

	public deleteLease(leaseId: number): Observable<any> {
		return this.httpClient.delete<any>(this.url + '/' + leaseId);
	}

	public getLatestLeasesByCarMarks(carMarks: string[]): Observable<RailcarLease[]> {
		return this.httpClient.post<RailcarLease[]>(this.url + '/getLatestLeasesByCarMarks', carMarks);
	}
	
	public GetPreviousLeaseDetailsByRailcarIds(leaseId: number, railcarIds: number[]): Observable<RailcarLease[]> {
		return this.httpClient.post<RailcarLease[]>(this.url + `/getPreviousLeaseDetailsByRailcarIds?leaseId=${leaseId}`, railcarIds);
	}

	public GetNextLeaseDetailsByRailcarIds(leaseId: number, railcarIds: number[]): Observable<RailcarLease[]> {
		return this.httpClient.post<RailcarLease[]>(this.url + `/getNextLeaseDetailsByRailcarIds?leaseId=${leaseId}`, railcarIds);
	}

	public getFromLeaseByRailcarIdAndDateInStorage( railcarId : number,  dateInStorage: string):Observable<Lease> {
		return this.httpClient.get<Lease>(this.url+ '/getFromLeaseByRailcarIdAndDateInStorage?railcarId='+railcarId+'&dateInStorage='+dateInStorage);
	}
}
